import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { GateLine, gateAction, useLimitLine } from "../ds/gate"
import { useLocation, useNavigate } from "react-router-dom"
import { botCheckMessage } from "@/lib/turnstile"
import { GUEST_BASE, guestSearchUsed, rememberGuestQuestion, markGuestSearchUsed, markGuestThread, isLoginRequired, useAuthGate } from "@/lib/authGate"
import type { StreamEvent } from "../stream"
import type { RouteView, ThreadRow } from "../contract"
import { attachThread, createThread, deleteDocument, followDocuments, routeQuestion, uploadDocuments } from "../api"
import { RouteCard } from "../answer/RouteCard"
import { track } from "@/lib/analytics"
import { useDocsStore } from "../docs.store"
import { NiraBar } from "../ds/NiraBar"
import { usePageTitle, useStoredTheme } from "../answer/hooks"
import { Rail } from "../answer/Rail"
import "../answer/answer.css"
import "../answer/answer-v4.css"
import { useBeamWhile } from "../beam"
import { contextFrom, useReaderContext } from "../readerContext"
import { AttachTray, type TrayItem } from "./AttachTray"
import { Composer, type ComposerHandle } from "./Composer"
import { Doors, type Door } from "./Doors"
import { NoticeDrop, type NoticeDropHandle } from "./NoticeDrop"
import { WorkspaceRead } from "./WorkspaceRead"
import { openPaywall, uploadRefusalMessage } from "@/lib/quotaGate"
import { useMeStore } from "@/features/auth/hooks/useCurrentUser"
import { prefersReducedMotion } from "./motion"
import { DueThisWeek } from "./DueThisWeek"
import { StarterQuestions, TYPED_EXAMPLES } from "./StarterQuestions"
import { InsideTeaser } from "./InsideTeaser"
import { NlSection } from "./NlSection"
import "./landing.css"

const MAX_FILES = 10
// Signed out, the home page is a guest's: no history, no workspaces.
const NO_THREADS = async (): Promise<ThreadRow[]> => []
const RECONNECT_DELAYS = [1000, 2000, 4000]
const RECONNECT_STEADY = 10000
const ALLOWED = /\.(pdf|docx|png|jpe?g|txt|md)$/i
// While an attached document is still being read, the router is asked again this often, this many times.
const ROUTE_POLL_MS = 2000
const ROUTE_POLLS = 90

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms)
    signal.addEventListener("abort", () => { clearTimeout(t); resolve() }, { once: true })
  })
}

/** Nira home (Home-v4): the question first, then the four doors and recent questions. */
export default function Landing() {
  usePageTitle(undefined)
  const navigate = useNavigate()
  const location = useLocation()
  const { signedIn, requireLogin } = useAuthGate()
  const signedInRef = useRef(signedIn)
  signedInRef.current = signedIn
  // A guest's question carried over after sign-in: shown in the composer, never auto-submitted.
  const [text, setText] = useState(() => {
    const p = (location?.state as { prefill?: unknown } | null)?.prefill
    return typeof p === "string" ? p : ""
  })
  // "Ask Nira" on a Matter page lands here carrying the Matter id: the thread joins it when the first question or file is sent.
  // The Workspaces hub's start buttons arrive as /ask?workspace=<id>; the same id can come in as router state.
  const matterId = useRef<string>((() => {
    const m = (location?.state as { matter?: unknown } | null)?.matter
    if (typeof m === "string" && m) return m
    return new URLSearchParams(window.location.search).get("workspace") ?? ""
  })())
  // /ask?workspace=…&door=notice|appeal&doc=…: the document of the workspace the reading is on.
  const docParam = useRef<string>(new URLSearchParams(window.location.search).get("doc") ?? "")
  const [draft, setDraft] = useState(false)
  const [docIds, setDocIds] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [feedLost, setFeedLost] = useState(false)
  const [note, setNote] = useState("")
  // /ask?history=1 (the site header's History link) opens the history drawer on arrival.
  const [histOpen, setHistOpen] = useState(() => signedIn && new URLSearchParams(window.location.search).get("history") === "1")
  // A notice or an order sent as a research question: its own door (nira/agent/route.py).
  const [route, setRoute] = useState<RouteView | null>(null)
  const [routing, setRouting] = useState(false)
  const preferred = useReaderContext((s) => s.preferred)
  const prefer = useReaderContext((s) => s.prefer)
  const docs = useDocsStore((s) => s.docs)
  const composer = useRef<ComposerHandle>(null)
  // Which door the box above serves: a question, or a notice to drop.
  // A link from the public site can open a door: /ask?door=notice or /ask?door=appeal.
  const [door, setDoor] = useState<Door>(() => {
    const asked = new URLSearchParams(window.location.search).get("door")
    return asked === "notice" || asked === "appeal" ? asked : "research"
  })
  const noticeDrop = useRef<NoticeDropHandle>(null)
  // /ask?upgrade=1 (the pricing page's Upgrade to Pro): open the Pro paywall here, once.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get("upgrade") !== "1") return
    openPaywall(useMeStore.getState().me?.entitlements ?? null, "nira.officer_reading", { upgrade: true })
    params.delete("upgrade")
    const rest = params.toString()
    window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`)
  }, [])
  // Every arrival starts at the top. The drop box takes focus when a door was asked for (from an answer,
  // or the public site). The question box takes focus only for /ask?focus=ask (the site footer's Ask a
  // question), so a plain visit does not open the keyboard on a phone. Only on arrival; switching doors
  // later focuses through the door buttons.
  useEffect(() => {
    window.scrollTo(0, 0)
    const params = new URLSearchParams(window.location.search)
    if (params.get("focus") === "ask") {
      params.delete("focus")
      const rest = params.toString()
      window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`)
      if (door === "research") composer.current?.focus()
    }
    if (door !== "research") noticeDrop.current?.focus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const thread = useRef<Promise<string> | null>(null)
  const mounted = useRef(false)
  const abort = useRef<AbortController | null>(null)
  const following = useRef(false)
  const attached = useRef<string[]>([])
  const inflight = useRef(0)
  const queue = useRef<Promise<void>>(Promise.resolve())
  const noteTimer = useRef(0)
  const typeTimer = useRef(0)
  const autoTimer = useRef(0)
  const submitRef = useRef<() => void>(() => {})

  const say = useCallback((msg: string) => {
    setNote(msg)
    clearTimeout(noteTimer.current)
    noteTimer.current = window.setTimeout(() => setNote(""), 2500)
  }, [])

  const setAttached = useCallback((next: string[]) => {
    attached.current = next
    setDocIds(next)
  }, [])

  useStoredTheme()

  useEffect(() => {
    const st = location?.state as { prefill?: unknown; ask?: unknown; files?: unknown } | null
    if (st && (st.prefill !== undefined || st.ask !== undefined || st.files !== undefined || (st as { matter?: unknown }).matter !== undefined)) {
      navigate(location.pathname, { replace: true, state: null })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  // Signing in ends guest mode: drop the cached guest thread so the next search creates an account thread.
  useEffect(() => {
    if (signedIn) thread.current = null
  }, [signedIn])

  useEffect(() => {
    mounted.current = true
    abort.current = new AbortController()
    return () => {
      mounted.current = false
      abort.current?.abort()
      clearTimeout(noteTimer.current)
      clearInterval(typeTimer.current)
      clearTimeout(autoTimer.current)
    }
  }, [])

  const hasPendingDocs = () =>
    attached.current.some((id) => {
      const st = useDocsStore.getState().docs[id]?.status
      return st !== "ready" && st !== "failed"
    })

  /** Follow document progress; reconnect with backoff while any attached doc is unfinished. */
  const runFollow = useCallback(async (threadId: string) => {
    const signal = abort.current?.signal
    if (!signal || following.current || signal.aborted) return
    following.current = true
    let fails = 0
    const onEvent = (e: StreamEvent) => {
      fails = 0
      setFeedLost(false)
      useDocsStore.getState().apply(e)
    }
    try {
      while (!signal.aborted) {
        try {
          await followDocuments(threadId, onEvent, signal)
        } catch {
          if (signal.aborted) break
        }
        if (signal.aborted) break
        if (!hasPendingDocs()) {
          setFeedLost(false)
          break
        }
        fails += 1
        if (fails > RECONNECT_DELAYS.length) setFeedLost(true)
        await sleep(RECONNECT_DELAYS[fails - 1] ?? RECONNECT_STEADY, signal)
      }
    } finally {
      following.current = false
    }
  }, [])

  /** Create the thread once; subscribe to its document feed only while still mounted. */
  const ensureThread = useCallback((): Promise<string> => {
    if (!thread.current) {
      const guest = !signedInRef.current
      const ctx = useReaderContext.getState().preferred
      const caseId = guest ? null : matterId.current || null
      thread.current = (guest ? createThread(undefined, undefined, GUEST_BASE) : createThread(undefined, ctx, undefined, { caseId })).then((row) => {
        if (guest) markGuestThread(row.id)
        else useReaderContext.getState().setThread(row.id, contextFrom(row) ?? ctx)
        if (!guest && matterId.current) void attachThread(matterId.current, row.id).catch(() => undefined)
        if (mounted.current) void runFollow(row.id)
        return row.id
      })
      thread.current.catch(() => { thread.current = null })
    }
    return thread.current
  }, [runFollow])

  const handleFiles = useCallback((picked: File[]) => {
    if (!signedInRef.current) { requireLogin("upload"); return }
    const ok = picked.filter((f) => ALLOWED.test(f.name))
    const badType = picked.length - ok.length
    const room = Math.max(0, MAX_FILES - attached.current.length - inflight.current)
    const files = ok.slice(0, room)
    const over = ok.length - files.length
    if (badType > 0 && over > 0) say(`Skipped some files. Use PDF, Word, image, text or Markdown, up to ${MAX_FILES} per question.`)
    else if (over > 0) say(`Up to ${MAX_FILES} files per question.`)
    else if (badType > 0) say("Use PDF, Word, image, text or Markdown files.")
    if (files.length === 0) return
    inflight.current += files.length
    setUploading(true)
    queue.current = queue.current.then(async () => {
      try {
        const id = await ensureThread()
        const rows = await uploadDocuments(id, files)
        useDocsStore.getState().addUploads(rows)
        setAttached([...attached.current, ...rows.map((r) => r.doc_id)])
        if (mounted.current) void runFollow(id)
      } catch (err) {
        say(uploadRefusalMessage(err) ?? "Couldn't upload those files. Try again.")
      } finally {
        inflight.current -= files.length
        setUploading(inflight.current > 0)
      }
    })
  }, [ensureThread, requireLogin, runFollow, say, setAttached])

  /* What the public site hands over (site/SiteGlimpse.tsx), once, on arrival:
     - router state {ask}: a question asked in the site's box on this origin, sent at once (router
       state can only come from our own pages);
     - router state {files}: a notice attached there, uploaded here;
     - ?ask= in the URL: the site on its own origin. It fills the box and is never sent by itself, so a
       link can never spend a reader's questions for them. ?q= does the same (an article's "Ask Nira
       about this" link): a guest has one question, so a link only ever fills the box. */
  const carried = useRef(false)
  useEffect(() => {
    const st = location?.state as { ask?: unknown; files?: unknown } | null
    if (!carried.current) {
      carried.current = true
      const params = new URLSearchParams(window.location.search)
      const linked = (params.get("ask") || params.get("q") || "").trim()
      if (linked) {
        setText(linked.slice(0, 8000))
        params.delete("ask")
        params.delete("q")
        const rest = params.toString()
        window.history.replaceState(null, "", `${window.location.pathname}${rest ? `?${rest}` : ""}`)
        window.requestAnimationFrame(() => composer.current?.focus())
      }
      const files = Array.isArray(st?.files) ? (st.files as unknown[]).filter((f): f is File => f instanceof File) : []
      if (files.length) handleFiles(files)
    }
    // The send has its own timer and cleanup, so a remount (React's development double mount) still sends once.
    if (typeof st?.ask === "string" && st.ask.trim()) {
      setText(st.ask.slice(0, 8000))
      const t = window.setTimeout(() => submitRef.current(), 300)
      return () => clearTimeout(t)
    }
    return undefined
    // Only on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeDoc = useCallback(async (id: string) => {
    const index = attached.current.indexOf(id)
    if (index < 0) return
    setAttached(attached.current.filter((d) => d !== id))
    composer.current?.focus()
    try {
      await deleteDocument(id)
    } catch {
      if (!attached.current.includes(id)) {
        const next = [...attached.current]
        next.splice(Math.min(index, next.length), 0, id)
        setAttached(next)
      }
      say("Couldn't remove that file. Try again.")
    }
  }, [say, setAttached])

  /** Ask the router where the question belongs, waiting while an attached document is still read.
   * Null to send it as research. The server decides again when the turn is sent. */
  const askRoute = useCallback(async (threadId: string, question: string, ids: string[]): Promise<RouteView | null> => {
    setRouting(true)
    try {
      for (let i = 0; i < ROUTE_POLLS; i += 1) {
        let decided
        try {
          decided = await routeQuestion(threadId, question, ids)
        } catch {
          return null
        }
        if (decided.route) {
          track("research_routed", { to: decided.route.to, why: decided.route.why })
          return decided.route
        }
        if (!decided.pending) return null
        say("Reading your document first…")
        const signal = abort.current?.signal
        if (!signal || signal.aborted) return null
        await sleep(ROUTE_POLL_MS, signal)
      }
      return null
    } finally {
      setRouting(false)
    }
  }, [say])

  // Used up with nothing to offer (the first month's 10 a day, Pro's daily limit): said under the box, no modal.
  const limitLine = useLimitLine("nira.message")
  // A paywall put off with "Not now": the same line instead of the modal, for the rest of the session.
  const [paused, setPaused] = useState("")
  const submit = useCallback(async () => {
    const question = text.trim()
    if (!question) { say("Type a question to start."); return }
    if (uploading) { say("Your files are still uploading."); return }
    if (limitLine) return
    setPaused("")
    const gate = await gateAction("nira.message")
    if (!gate.ok) { setPaused(gate.line); return }
    clearInterval(typeTimer.current)
    clearTimeout(autoTimer.current)
    if (!signedIn && guestSearchUsed()) { requireLogin("landing_search"); return }
    try {
      const id = await ensureThread()
      if (!signedIn) { markGuestSearchUsed(); rememberGuestQuestion(question) }
      const attachedDocIds = attached.current.filter((d) => docs[d]?.status !== "failed")
      if (signedIn) {
        const routed = await askRoute(id, question, attachedDocIds)
        if (routed) { setRoute(routed); return }
      }
      navigate(`/nira/${id}`, { state: { question, mode: draft && signedIn ? "draft_check" : "ask", docIds: signedIn ? attachedDocIds : [] } })
    } catch (err) {
      if (isLoginRequired(err)) requireLogin("login_required")
      else say(botCheckMessage(err) ?? "Couldn't start. Try again.")
    }
  }, [text, uploading, docs, draft, ensureThread, navigate, say, signedIn, requireLogin, askRoute, limitLine])
  submitRef.current = () => { void submit() }

  /** "Ask on the law, without it": the same question on a fresh thread with nothing attached. */
  const askWithout = useCallback(() => {
    setRoute(null)
    setAttached([])
    thread.current = null
    window.requestAnimationFrame(() => submitRef.current())
  }, [setAttached])

  const typeInto = useCallback((full: string) => {
    setDraft(false)
    clearInterval(typeTimer.current)
    clearTimeout(autoTimer.current)
    if (prefersReducedMotion()) {
      setText(full)
      composer.current?.focus()
      autoTimer.current = window.setTimeout(() => submitRef.current(), 300)
      return
    }
    let i = 0
    setText("")
    typeTimer.current = window.setInterval(() => {
      i += 1
      setText(full.slice(0, i))
      if (i >= full.length) {
        clearInterval(typeTimer.current)
        composer.current?.focus()
        composer.current?.select(full.length)
        autoTimer.current = window.setTimeout(() => submitRef.current(), 520)
      }
    }, 18)
  }, [])

  const stopAuto = useCallback(() => {
    clearInterval(typeTimer.current)
    clearTimeout(autoTimer.current)
  }, [])

  /** A draft check reads an attached or pasted draft reply against the law; the text is kept. */
  const toggleDraft = useCallback(() => {
    stopAuto()
    setDraft((d) => !d)
    composer.current?.focus()
  }, [stopAuto])

  const items: TrayItem[] = useMemo(
    () => docIds.flatMap((id) => (docs[id] ? [{ id, doc: docs[id] }] : [])),
    [docIds, docs],
  )

  // A door changes the box in place; the reader's eye goes back to it.
  const chooseDoor = useCallback((next: Door) => {
    setDoor(next)
    window.requestAnimationFrame(() => {
      if (next !== "research") noticeDrop.current?.focus()
      else composer.current?.focus()
    })
  }, [])

  // A guest sent to a door is asked to sign in first; the box stays a question box meanwhile.
  useEffect(() => {
    if (door !== "research" && !signedIn) requireLogin("upload")
    // Only on arrival: later door changes go through chooseDoor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // The beam listens while the reader types or a file uploads.
  useBeamWhile("listening", text.length > 0 || uploading)

  return (
    <div className="nl-landing nh">
      <div className="na2 na4 nh-drawer">
        <Rail threadId="" scope={door} histOpen={histOpen} onHist={setHistOpen} refreshKey={0} drawerOnly {...(signedIn ? {} : { loadThreads: NO_THREADS, showMatters: false })} />
        <div className={`scrim${histOpen ? " on" : ""}`} aria-hidden="true" onClick={() => setHistOpen(false)} />
      </div>
      <NiraBar
        onMenu={() => setHistOpen((v) => !v)}
        menuOpen={histOpen}
        menuControls="nira-hlist"
        nav={{ signedIn }}
        context={{ value: preferred, onChange: prefer }}
      />
      <main className="nl-main">
        <section className="nl-first" aria-label="Ask Nira">
          <div className="nl-stack">
            <p className="nl-overline" data-fade>GST research · with the judgment behind it</p>
            <h1 data-fade>Where you stand, and what it could cost.</h1>
            <p className="nl-sub" data-fade>What the law says, which decisions help and hurt, how strongly they bind in your State, the rupees, and what to do by when.</p>
            {signedIn ? <DueThisWeek /> : null}
            {door !== "research" && signedIn && matterId.current && docParam.current ? (
              <WorkspaceRead key={door} caseId={matterId.current} doc={docParam.current} kind={door === "appeal" ? "order" : "notice"} onAsk={() => chooseDoor("research")} />
            ) : door !== "research" && signedIn ? (
              <NoticeDrop key={door} ref={noticeDrop} kind={door === "appeal" ? "order" : "notice"} onAsk={() => chooseDoor("research")} />
            ) : (
              <Composer
                ref={composer}
                value={text}
                onChange={(v) => { setText(v); setRoute(null) }}
                onSubmit={() => { void submit() }}
                onFiles={handleFiles}
                busy={uploading || !!limitLine || routing}
                note={note}
                hint={signedIn ? <><AllowanceHint metric="nira.message" className="ngl-hint" /><GateLine text={limitLine || paused} /></> : null}
                onUserInput={stopAuto}
                draft={draft}
                onLeaveDraft={toggleDraft}
                reads
                typed={TYPED_EXAMPLES}
                tray={
                  <>
                    <AttachTray items={items} notice={feedLost ? "Lost the progress feed. Retrying." : ""} onRemove={(id) => { void removeDoc(id) }} />
                    {items.length && !draft && signedIn ? (
                      <button type="button" className="nh-draftchip" onClick={toggleDraft}>
                        Is this your draft reply? Check it against the law
                      </button>
                    ) : null}
                  </>
                }
              />
            )}
            {route && door === "research" ? (
              <RouteCard
                route={route}
                onWithout={route.why === "document" ? askWithout : () => { setRoute(null); composer.current?.focus() }}
                onDoor={(d) => { setRoute(null); chooseDoor(d) }}
              />
            ) : null}
          </div>
          {door === "research" ? (
            <aside className="nl-examples" aria-label="Examples" data-fade>
              <StarterQuestions onPick={typeInto} />
            </aside>
          ) : null}
        </section>

        <div className="nl-wrap nl-more" data-fade>
          <Doors door={door} onDoor={chooseDoor} />

          <NlSection id="how" eyebrow="How it works" title="Ask it the way you would ask a colleague.">
            <ol className="nl-steps">
              <li><span className="nl-n">01</span><h3>Ask in your words</h3><p>Type the question the way you would put it to a colleague.</p></li>
              <li><span className="nl-n">02</span><h3>NIRA reads the decisions</h3><p>It searches judgments, the Acts and circulars, and keeps what applies.</p></li>
              <li><span className="nl-n">03</span><h3>See the answer with proof</h3><p>Every point links to the paragraph it came from.</p></li>
            </ol>
          </NlSection>

          <InsideTeaser />
        </div>
      </main>

      <footer className="nl-wrap nl-foot" data-fade>
        <span>Answers from court decisions, the GST Acts and CBIC circulars. Research, not legal advice. Check the cited text before you rely on it.</span>
        <span className="nl-foot-r"><a href="/">About Nira</a> · <a href="/how-we-work">How we work</a> · <span className="nl-yr">2026</span></span>
      </footer>
    </div>
  )
}

export { Landing }
